"use client";

import { type ReactNode, useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { DensityResults } from "@/components/tools/chemistry/density-results";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  DENSITY_UNITS,
  type DensitySolve,
  densityToBase,
  FLUIDS,
  REFERENCE_DENSITIES,
  referenceDensity,
  solveDensity,
} from "@/lib/tool-kit/chemistry/density";
import { fewestSigFigs, formatPlain } from "@/lib/tool-kit/chemistry/format";
import {
  MASS,
  toBase,
  unitOptions,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const DENSITY_CALCULATOR_STRINGS = {
  solveLabel: "Solve for",
  solveDensity: "Density",
  solveMass: "Mass",
  solveVolume: "Volume",
  massLabel: "Mass",
  volumeLabel: "Volume",
  densityLabel: "Density",
  answerSuffix: "{label} (answer)",
  massUnit: "Mass unit",
  volumeUnit: "Volume unit",
  densityUnit: "Density unit",
  fluidLabel: "Sink or float? Compare with",
  fluidNone: "Do not compare",
  fluidCustom: "Another fluid (enter its density)",
  fluidDensity: "Fluid density (g/mL)",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  answerDensity: "Density",
  answerMass: "Mass",
  answerVolume: "Volume",
  specificGravity: "Specific gravity (water at 4 °C = 1)",
  closeTo:
    "Close to {material} ({density} g/mL). Density alone cannot identify a material: alloys, porosity and temperature all shift it.",
  verdictFloats: "It floats in {fluid}.",
  verdictSinks: "It sinks in {fluid}.",
  verdictNeutral: "It is neutrally buoyant in {fluid}.",
  floatDetail: "Object {object} g/mL against fluid {fluid} g/mL.",
  submerged: "About {percent}% of its volume sits below the surface.",
  working: "The working",
  equation: "ρ = m ÷ V",
  workDensity: "ρ = {mass} g ÷ {volume} mL = {density} g/mL",
  workMass: "m = ρ × V = {density} g/mL × {volume} mL = {mass} g",
  workVolume: "V = m ÷ ρ = {mass} g ÷ {density} g/mL = {volume} mL",
  equivalentsHeading: "The density in other units",
  columnUnit: "Unit",
  columnValue: "Value",
  referenceHeading: "Reference densities",
  referenceNote:
    "Typical values near 20 °C from the CRC Handbook of Chemistry and Physics; seawater is approximate (35 g/kg salinity) and air is dry air at 101.325 kPa. Real samples vary.",
  columnMaterial: "Material",
  columnGmL: "g/mL (g/cm³)",
  columnKgM3: "kg/m³",
  enterValues: "Enter the other two quantities to see the answer.",
  invalid: "Each quantity must be a number greater than zero.",
  copy: "Copy results",
  copied: "Copied!",
  copyText: "Density {density} (mass {mass}, volume {volume})",
  materials: {
    air: "Air (dry)",
    ethanol: "Ethanol",
    ice: "Ice (0 °C)",
    water: "Water (20 °C)",
    seawater: "Seawater",
    glycerol: "Glycerol",
    aluminum: "Aluminum",
    iron: "Iron",
    copper: "Copper",
    silver: "Silver",
    lead: "Lead",
    mercury: "Mercury",
    gold: "Gold",
  },
} as const;

type Strings = typeof DENSITY_CALCULATOR_STRINGS;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const ML_PER_L = 1000;
const MASS_UNITS = unitOptions(MASS);
const VOLUME_UNITS = unitOptions(VOLUME);
const DENSITY_UNIT_OPTIONS = DENSITY_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const KG_M3_PER_G_ML = 1000;

interface Quantity {
  text: string;
  unit: string;
}

type Field = "mass" | "volume" | "density";

const START: Record<Field, Quantity> = {
  mass: { text: "54.0", unit: "g" },
  volume: { text: "20.0", unit: "cm3" },
  density: { text: "", unit: "gcm3" },
};

function readBase(field: Field, q: Quantity): number | null {
  const value = parseMeasuredNumber(q.text);
  if (q.text.trim() === "" || value === null) {
    return null;
  }
  if (field === "mass") {
    return toBase(value, MASS, q.unit);
  }
  if (field === "volume") {
    return toBase(value, VOLUME, q.unit) * ML_PER_L;
  }
  return densityToBase(value, q.unit);
}

function ReferenceTable({ strings }: { strings: Strings }) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.referenceHeading}</h3>
      <DataTable
        head={[strings.columnMaterial, strings.columnGmL, strings.columnKgM3]}
        rows={REFERENCE_DENSITIES.map((ref) => [
          strings.materials[ref.id as keyof typeof strings.materials],
          formatPlain(fmt, ref.density),
          formatPlain(
            fmt,
            Number((ref.density * KG_M3_PER_G_ML).toPrecision(4))
          ),
        ])}
      />
      <p className="text-muted-foreground text-xs">{strings.referenceNote}</p>
    </div>
  );
}

function fluidChoice(
  strings: Strings,
  fluid: string,
  customText: string
): { density: number | null; name: string } {
  if (fluid === "none") {
    return { density: null, name: "" };
  }
  if (fluid === "custom") {
    const value = parseMeasuredNumber(customText);
    return {
      density: value !== null && value > 0 ? value : null,
      name: strings.fluidCustom,
    };
  }
  return {
    density: referenceDensity(fluid),
    name: strings.materials[fluid as keyof typeof strings.materials],
  };
}

function Panel({
  solve,
  q,
  sig,
  fluid,
  strings,
}: {
  solve: DensitySolve;
  q: Record<Field, Quantity>;
  sig: number;
  fluid: { density: number | null; name: string };
  strings: Strings;
}): ReactNode {
  const needed: Field[] = (["mass", "volume", "density"] as const).filter(
    (field) => field !== solve
  );
  if (needed.some((field) => q[field].text.trim() === "")) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  const values = solveDensity(solve, {
    massG: solve === "mass" ? null : readBase("mass", q.mass),
    volumeML: solve === "volume" ? null : readBase("volume", q.volume),
    density: solve === "density" ? null : readBase("density", q.density),
  });
  if (values === null) {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  return (
    <DensityResults
      fluid={fluid}
      sig={sig}
      solve={solve}
      strings={strings}
      units={{
        mass: q.mass.unit,
        volume: q.volume.unit,
        density: q.density.unit,
      }}
      v={values}
    />
  );
}

export default function DensityCalculatorWidget() {
  const strings = useToolUi(DENSITY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [solve, setSolve] = useState<DensitySolve>("density");
  const [q, setQ] = useState<Record<Field, Quantity>>(START);
  const [sigChoice, setSigChoice] = useState("match");
  const [fluid, setFluid] = useState("water");
  const [customText, setCustomText] = useState("");
  const set = (field: Field, patch: Partial<Quantity>) =>
    setQ({ ...q, [field]: { ...q[field], ...patch } });

  const entries = (["mass", "volume", "density"] as const)
    .filter((field) => field !== solve)
    .map((field) => q[field].text);
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(entries, DEFAULT_SIG)
      : Number(sigChoice);

  const field = (
    name: Field,
    label: string,
    unitLabel: string,
    units: { value: string; label: string }[]
  ) => (
    <QuantityInput
      id={`den-${name}`}
      label={solve === name ? t(strings.answerSuffix, { label }) : label}
      onUnitChange={(unit) => set(name, { unit })}
      onValueChange={(text) => set(name, { text })}
      solved={solve === name}
      unit={q[name].unit}
      unitLabel={unitLabel}
      units={units}
      value={q[name].text}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="den-solve"
        onChange={setSolve}
        options={[
          { value: "density", label: strings.solveDensity },
          { value: "mass", label: strings.solveMass },
          { value: "volume", label: strings.solveVolume },
        ]}
        value={solve}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {field("mass", strings.massLabel, strings.massUnit, MASS_UNITS)}
        {field("volume", strings.volumeLabel, strings.volumeUnit, VOLUME_UNITS)}
        {field(
          "density",
          strings.densityLabel,
          strings.densityUnit,
          DENSITY_UNIT_OPTIONS
        )}
        <SelectField
          id="den-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="den-fluid"
          label={strings.fluidLabel}
          onChange={setFluid}
          options={[
            { value: "none", label: strings.fluidNone },
            ...FLUIDS.map((id) => ({
              value: id,
              label: strings.materials[id as keyof typeof strings.materials],
            })),
            { value: "custom", label: strings.fluidCustom },
          ]}
          value={fluid}
        />
        {fluid === "custom" ? (
          <TextField
            id="den-fluid-density"
            label={strings.fluidDensity}
            numeric
            onChange={setCustomText}
            value={customText}
          />
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Panel
          fluid={fluidChoice(strings, fluid, customText)}
          q={q}
          sig={sig}
          solve={solve}
          strings={strings}
        />
        <ReferenceTable strings={strings} />
      </section>
    </>
  );
}
